import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';

function formatRequestedDate(value: string | null) {
  if (!value) return 'Recently';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ProfileAccessRequestModal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const accessRequestId = searchParams.get('accessRequest');
  const recruiterEmail = searchParams.get('recruiterEmail')?.trim() || '';
  const recruiterCompany = searchParams.get('recruiterCompany')?.trim() || '';
  const recruiterName =
    searchParams.get('recruiterName')?.trim() ||
    recruiterEmail.split('@')[0].replace('.', ' ').replace('_', ' ');
  const requestedAt = searchParams.get('requestedAt');
  const requestedField = searchParams.get('requestedField')?.trim() || '';
  const [busy, setBusy] = useState(false);

  const sectionLabels: Record<string, string> = {
    email: 'email',
    phoneNumber: 'phone number',
    location: 'location',
    salary: 'salary expectations',
    company: 'current company',
    calendar: 'calendar availability',
    dob: 'date of birth',
    workArrangements: 'work arrangements',
    startTime: 'time to start',
  };
  const isSectionRequest = Boolean(requestedField && requestedField !== 'full_access');
  const sectionLabel = sectionLabels[requestedField] || requestedField.replace(/_/g, ' ');

  const show = Boolean(accessRequestId);

  const close = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('accessRequest');
        next.delete('recruiterEmail');
        next.delete('recruiterCompany');
        next.delete('recruiterName');
        next.delete('requestedAt');
        next.delete('requestedField');
        return next;
      },
      { replace: true }
    );
  };

  const respond = async (action: 'accept' | 'deny') => {
    if (!recruiterEmail) {
      toast.error(
        'Recruiter email is missing for this request. Open it from your notifications bell instead.'
      );
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.post(API_ROUTES.RECRUITER.RESPOND_ACCESS_REQUEST, {
        recruiterEmail,
        action,
        accessRequestId,
      });
      toast.success(
        action === 'accept'
          ? isSectionRequest
            ? `${sectionLabel.charAt(0).toUpperCase()}${sectionLabel.slice(1)} access approved.`
            : 'Full Access approved. The recruiter will receive an email with their access link.'
          : 'Access request declined.'
      );
      close();
    } catch {
      toast.error('Could not update this access request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isSectionRequest
            ? `${sectionLabel.charAt(0).toUpperCase()}${sectionLabel.slice(1)} access request`
            : 'Full Access request'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          <strong>{recruiterName}</strong>
          {recruiterCompany ? ` from ${recruiterCompany}` : ''} is requesting
          {isSectionRequest
            ? ` access to your ${sectionLabel}.`
            : ' Full Access to your SkipScreen profile.'}
        </p>
        <dl className="row mb-0 small">
          <dt className="col-sm-4">Full name</dt>
          <dd className="col-sm-8">{recruiterName}</dd>
          <dt className="col-sm-4">Email</dt>
          <dd className="col-sm-8">{recruiterEmail || '—'}</dd>
          <dt className="col-sm-4">Company</dt>
          <dd className="col-sm-8">{recruiterCompany || 'Unverified visitor'}</dd>
          <dt className="col-sm-4">Date requested</dt>
          <dd className="col-sm-8">{formatRequestedDate(requestedAt)}</dd>
        </dl>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" onClick={close} disabled={busy}>
          Close
        </button>
        <button
          type="button"
          className="btn btn-outline-danger"
          disabled={busy}
          onClick={() => void respond('deny')}
        >
          Deny
        </button>
        <button
          type="button"
          className="btn btn-success"
          disabled={busy}
          onClick={() => void respond('accept')}
        >
          Approve
        </button>
      </Modal.Footer>
    </Modal>
  );
}
